import { useSyncExternalStore } from 'react';

/** Which of Help's sheets is open: Help itself, the keyboard shortcuts, or how Paige works. */
export type HelpSheetName = 'help' | 'shortcuts' | 'explainer';

export interface HelpView {
  sheet: HelpSheetName;
  /** The company the sheet speaks for: the tour and the explainer are its. */
  tenantId: string;
  /** What gets the keyboard back when the sheet closes (the profile's button; a menu row is gone by then). */
  back?: HTMLElement | null;
}

/**
 * Help, opened from the profile menu, the workbench's account door, F1 outside a job and the welcome's tour (gap
 * list C8; Dustin, 2026-10-20). One sheet at a time for the whole app, drawn by HelpHost beside every route, so a
 * row of a menu that has already closed can still open it, and the workbench reaches it through its bridge.
 */
let view: HelpView | null = null;
const listeners = new Set<() => void>();
const tell = () => listeners.forEach((listener) => listener());

export function openHelp(
  tenantId: string,
  sheet: HelpSheetName = 'help',
  back?: HTMLElement | null,
): void {
  // From one of Help's sheets to another, the keyboard still goes back to what opened Help.
  view = { sheet, tenantId, back: back === undefined ? (view?.back ?? null) : back };
  tell();
}

export function closeHelp(): void {
  view = null;
  tell();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/** The sheet open now, outside React (a key handler). */
export const currentHelp = (): HelpView | null => view;

/** The sheet open now, or null. */
export const useHelpView = (): HelpView | null => useSyncExternalStore(subscribe, () => view);
